import { Box, Link, Typography } from '@mui/material';
import AuthModal from 'components/system/AuthModal/AuthModal';
import { LoadingButton } from 'components/system/buttons';
import { OtpInput } from 'components/system/OtpInput';
import { SUPPORT_EMAIL } from 'constants/info';
import { Controller } from 'react-hook-form';
import { CountdownChip } from '../../system/CountdownChip/CountdownChip';
import { useCodeVerification } from '../hooks/useCodeVerification';
import { useRecaptchaVerifier } from '../hooks/useRecaptchaVerifier';
import { SetupAccountModal } from './common/SetupAccountModal';

export const CodeVerificationModal = ({
  isNewPhone,
  onCompleted,
  open = true,
}: {
  isNewPhone: boolean;
  onCompleted: () => void;
  open?: boolean;
}) => {
  const { containerRef, recaptchaVerifier } = useRecaptchaVerifier();

  const {
    handleBackToLogin,
    control,
    errors,
    sendCode,
    handleSubmit,
    loading,
    resendCodeSecondsLeft,
  } = useCodeVerification({ recaptchaVerifier, isNewPhone, onCompleted });

  return (
    <SetupAccountModal open={open} handleBackToLogin={handleBackToLogin}>
      <div ref={containerRef} style={{ visibility: 'hidden' }} />
      <AuthModal.Content>
        <AuthModal.Title sx={{ marginBottom: 4 }}>
          Code Verification
        </AuthModal.Title>
        <Typography variant="h6" component="p" sx={{ marginBottom: 4 }}>
          Please enter the code sent to your phone number.
        </Typography>
        <Typography variant="textSm" sx={{ marginBottom: 12 }} paragraph>
          Can't verify your code? Contact support{' - '}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
        </Typography>
        <Controller
          name="code"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <OtpInput
              {...field}
              autoFocus
              sx={{ marginBottom: 4 }}
              errorMessage={errors.code?.message}
            />
          )}
        />

        {resendCodeSecondsLeft ? (
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="textSm" sx={{ display: 'inline' }}>
              Resend code in:
            </Typography>
            <CountdownChip secondsLeft={resendCodeSecondsLeft} />
          </Box>
        ) : (
          <Typography variant="textSm" sx={{ textAlign: 'center' }}>
            Didn't receive the code?{' '}
            <Link
              role="button"
              sx={{ cursor: 'pointer' }}
              tabIndex={0}
              onClick={sendCode}
            >
              Resend code
            </Link>
          </Typography>
        )}
      </AuthModal.Content>
      <AuthModal.Buttons>
        <LoadingButton onClick={handleSubmit} loading={loading}>
          Submit
        </LoadingButton>
      </AuthModal.Buttons>
    </SetupAccountModal>
  );
};
